
import Vue from 'vue';
import FingerprintJs from '@fingerprintjs/fingerprintjs';
import moment from 'moment-timezone';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	SNACKBAR_STATUS,
	READY_SESSION_STATES,
	NEW_USER_PROMO_END_DATE,
	NEW_USER_PROMO_START_DATE,
	BOOTING_SESSION_STATES,
} from '@/constants/constants';
import {
	AppKey,
	StableDiffusionModel,
	User,
	Team,
	Fingerprint,
	APP_KEY,
	Announcement,
	SURVEY_KEY,
} from '@run-diffusion/shared';
import _keys from 'lodash/keys';
import _isEmpty from 'lodash/isEmpty';
import _debounce from 'lodash/debounce';
import {
	shouldRedirectFromUserNotActivated,
	shouldRedirectToEmailNotVerified,
	shouldRedirectToUserNotActivated,
	isRouteRunnitSideNavPage,
	isRouteRunnitBoardPage,
} from '@/router/utils';
import {
	getTeamId,
	getProxyAdminToken,
	getCivitaiModelVersionId,
	getAcceptedReferralCode,
	clearAllGlobalSearchRecents,
	getAnalyticsIdentityFingerprint,
	setAnalyticsIdentityFingerprint,
	removeAnalyticsIdentityFingerprint,
} from '@/utils/localStorage';
import {
	LaunchedSessionTimerMixin,
	TeamUserMixin,
	VuexFirestoreBindingsMixin,
} from '@/mixins';
import { auth, db, functions, FieldValue } from '@/firebase';
import type { FirebaseUser, FirebaseUserCredential } from '@/firebase';
import { getGtmCookies, persistGoogleAdsClickIdsFromUrl, getGoogleAdsClickIds, persistUtmAttributionFromUrl, hydrateAttributionFromSharedCookie, hydrateSessionAttributesFromNonce, persistSessionAttributesFromUrl, getStoredSessionAttributes, trackLinkedInFreeSignupConversion } from '@/utils/googleTagManager';
import { removeAnnouncementDismissedAll } from '@/utils';
import { emailMatchesFallbackEnterpriseDomain } from '@/utils/enterpriseFallbackDomains';
import { BOARDS_TOUR_ID, boardsTourRequired, boardsTourSnoozed, boardsTourState, nextBoardsTourState } from '@/utils/boardsTour';
import { rememberBoardsVersion } from '@/utils/boardsVersion';
import Toolbar from '@/components/Toolbar/Toolbar.vue';
import Resources from '@/views/Resources/Resources.vue';
import GlobalLoader from '@/components/GlobalLoader.vue';
import AnnouncementsBanner from '@/components/Toolbar/AnnouncementsBanner.vue';
import GlobalSCSS from '@/components/Scss/GlobalSCSS.vue';
import RunnitsSCSS from '@/components/Scss/RunnitsSCSS.vue';
import GlobalSnackbar from '@/components/GlobalSnackbar/GlobalSnackbar.vue';
import SettingsBtn from '@/components/Toolbar/SettingsBtn.vue';
import SessionBar from '@/components/SessionBar/SessionBar.vue';
import BaseDrawerSCSS from '@/components/Scss/BaseDrawerSCSS.vue';
import UnsupportedDialog from '@/components/UnsupportedDialog.vue';
import TimeExpiringDialog from '@/components/TimeExpiringDialog.vue';
import AccountMenuBtn from '@/components/Toolbar/AccountMenuBtn.vue';
import FileBrowserBtn from '@/components/Toolbar/FileBrowserBtn.vue';
import NotificationsBtn from '@/components/Toolbar/NotificationsBtn.vue';
import UserAccount from '@/components/Toolbar/UserAccount/UserAccount.vue';
import ServerBuddy from '@/components/SessionBar/ServerBuddy/ServerBuddy.vue';
import FileBrowserNewTabBtn from '@/components/Toolbar/FileBrowserNewTabBtn.vue';
import GetSupportBtn from '@/components/Toolbar/GetSupportBtn/GetSupportBtn.vue';
import UpsellCreatorsClubDialog from '@/components/UpsellCreatorsClubDialog.vue';
import UpsellRunnitsPlanDialog from '@/components/UpsellRunnitsPlan/UpsellRunnitsPlanDialog.vue';
import MultipleSessions from '@/views/Sessions/CurrSessions/MultipleSessions.vue';
import AdminUserStats from '@/components/AdminStats/AdminUserStats/AdminUserStats.vue';
import NewUserLoginPromoDialog from '@/components/PromoBanners/NewUserLoginPromoDialog.vue';
import NewUserOnboardingDialog from '@/components/PromoBanners/NewUserOnboardingDialog.vue';
import NotificationsContainer from '@/components/NotificationsContainer/NotificationsContainer.vue';
import RunnitSideNav from '@/views/Runnits/RunnitSideNav/RunnitSideNav.vue';
import { APP_BAR_HEIGHT } from '@/components/Toolbar/constants';
import ComplexBackgroundMobileToolbar from '@/components/designElements/ComplexBackgroundMobileToolbar.vue';
import ConfigureRunnit from './views/Runnits/ConfigureRunnit.vue';
import RunnitTemplateDialog from './views/Runnits/RunnitTemplateDialog.vue';
import DeleteRunnitBoardDialog from './views/Runnits/DeleteRunnitBoardDialog.vue';
import RunnitManageAccessDialog from './views/Runnits/RunnitManageAccessDialog/RunnitManageAccessDialog.vue';
import { LoginQueryParamsDataState, RunnitsOwnerSelection, StoreLoadingState } from './store';
import { RUNNITS_OWNER_SELECTION } from './views/Runnits/constants';
import { getDomainClaimDialogContent, DomainClaimDialogContent } from '@/utils/domainClaimRestriction';
import { decideReconcileEntry } from '@/utils/membershipReconciliation';
import { noteTeamsAdded } from '@/utils/teamsMirrorLag';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import ListItem from '@/components/base/ListItem.vue';
import DomainClaimResolutionContent from '@/views/Runnits/RunnitSideNav/DomainClaimResolutionContent.vue';
import RunnitModelsUploadDialog from "@/views/Runnits/pages/models/RunnitModelsUploadDialog.vue";
import { Breakpoint } from 'vuetify/types/services/breakpoint';
import * as amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { analytics, SURFACES, buildSuperProps, buildPersonProps, accountAgeDaysFromMs } from '@/services/analytics';
import { identityFingerprint, shouldIdentify } from '@/services/analytics/identityDedupe';
import { clearHumanCheckPass, getHumanCheckPassIdForSignIn } from '@/services/humanCheck';
import { Route } from 'vue-router/types/router';
import { ROUTER } from '@/router/constants';
import { getPlatformHomePreference, PlatformHomePreference, setPlatformHomePreference } from '@/utils';
import VersionTracker from '@/components/VersionTracker.vue';
import DeviceAndLoginMethodProtector from '@/views/DeviceAndLoginMethodProtector.vue';
import Page404 from '@/views/Page404.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import RunnitBoardSkeleton from '@/views/Runnits/RunnitBoardSkeleton.vue';
import RunDiffusionWithRunnitMarkLogo from '@/assets/RunDiffusionWithRunnitMarkLogo.vue';

// Amplitude initialization state tracking
let isAmplitudeInitialized = false;
let isAmplitudeInitializedWithUserId = false;

declare global {
	interface Window {
		fpr: any; // FirstPromoter global
		_hsq: any[]; // HubSpot tracking queue
	}
}

export default Vue.extend({
	name: 'App',
	mixins: [
		VuexFirestoreBindingsMixin,
		TeamUserMixin,
		LaunchedSessionTimerMixin,
	],
	data () {
		return {
			APP_BAR_HEIGHT,
			loggingOut: false,
			capturedSignInData: false,
			// Last [userId, personProps] pair sent to analytics.identify(), so a
			// re-run of identifyAnalyticsUser with unchanged identity doesn't emit
			// another $set. Not reactive state anything renders; it just has to
			// live as long as the app session.
			lastAnalyticsIdentityFingerprint: null as string | null,
			deviceReportLocalPromise: null as Promise<void> | null,
			// Centered-modal fallback for the Domain Claim Resolution Dialog — shown only when
			// the sidenav flyout can't render (mobile / non-sidenav routes). Per-session dismiss.
			domainClaimModalDismissed: false,
			timeExpiringDialog: {
				open: false,
				minutesLeft: 0,
			},

			secondInterval: null,

			menuResetTrigger: 0,
			isEmailVerificationSent: false,
			shouldNewUserPromoOpen: false,
			shouldNewUserOnboardingOpen: false,
			hasCheckedNewUserOnboarding: false,
			// new accounts go through the Boards v2 tour after the survey (IL-095): masterConfigs/boardsV2.newAccounts
			boardsV2NewAccounts: false,
			debouncedUpdateScreenResizeBreakpointTrigger: () => { },
			addingToTeam: false,
			membershipReconcileDialogOpen: false,
			membershipReconcileTeams: [],
		};
	},
	async created () {
		// Hydrate attribution from shared cookie FIRST (cross-domain bridge from marketing site)
		// This pulls in UTMs and click IDs captured on rundiffusion.com before user navigated here
		hydrateAttributionFromSharedCookie();

		// Hydrate session attributes from shared nonce cookie (cross-domain bridge from marketing site)
		// This is async as it may fetch from Firestore via a callable function
		hydrateSessionAttributesFromNonce();

		// Then persist from URL params (allows app-side params to update last-touch)
		persistGoogleAdsClickIdsFromUrl();
		persistUtmAttributionFromUrl();
		persistSessionAttributesFromUrl();

		this.debouncedUpdateScreenResizeBreakpointTrigger = _debounce(() => {
			this.updateScreenResizeBreakpointTrigger(this.$vuetify.breakpoint);
		}, 100);
		window.addEventListener('resize', this.debouncedUpdateScreenResizeBreakpointTrigger);
		// Global Search: cmd/ctrl+K from anywhere in the app (industry standard —
		// works inside inputs too; the modal's own ESC handling closes it).
		window.addEventListener('keydown', this.onGlobalSearchKeydown);
		this.$autoSoundMachine.init();
		this.initSecondInterval();

		if (!this.toolbar.session) {
			this.updateToolbar({
				sessionBarOpen: false,
			});
		}

		// Refresh the existing token if not doing an auto SSO login
		if (!this.loginQueryParamsData.autoLoginSsoProvider) {
			auth.onIdTokenChanged((authUser: FirebaseUser) => {
				if (authUser && !authUser.emailVerified && !this.isEmailVerificationSent) {
					authUser.sendEmailVerification();
					this.isEmailVerificationSent = true;
				}
				if (authUser) {
					this.setAuthUser(authUser);
				} else {
					this.setAuthUser(null);
				}
			});
		}

		this.bindAuthUserDependentState(this.authUser, null, this.user);
		this.onUserChangeBinding(this.user, null);
		this.bindStableDiffusionModels();
		this.loadCivitaiPreloadModel();
		this.bindAnnouncements();
	},
	destroyed () {
		window.removeEventListener('resize', this.debouncedUpdateScreenResizeBreakpointTrigger);
		window.removeEventListener('keydown', this.onGlobalSearchKeydown);
	},
	computed: {
		...mapGetters([
			'isLoggedIn',
			'skipNewUserSurvey',
			'sessionAppKey',
			'activeDomainClaimRestriction',
			'showStripeUserMembershipPortalBtn',
			'displayLabels',
		]),
		activeSessionAppOffer () {
			// Look up the app offer for the toolbar's active session, deriving the AppKey from
			// session.apps[0] (legacy) or session.deploymentImage (Mobius).
			const session = this.toolbar && this.toolbar.session;
			if (!session || !this.appOffersMap) return null;
			const appKey: AppKey = this.sessionAppKey(session);
			return appKey ? (this.appOffersMap[appKey] || null) : null;
		},
		...mapState([
			'loadingUser',
			'loadingTeam',
			'loadingCompanyUserRole',
			'loadingTeamUserRole',
			'isAdminProxyingUser',
			'loginQueryParamsData',
			'user',
			'team',
			'stripeCustomer',
			'userBalanceAccount',
			'appOffersMap',
			'toolbar',
			'authUser',
			'stableDiffusionModels',
			'runnitState',
			'announcements',
			'loadingAnnouncements',
			'showMqlCalendarDialog',
			'deviceReportLocal',
		]),
		// Branched content for the Domain Claim Resolution Dialog (shared with the sidenav flyout).
		domainClaimModalContent (): DomainClaimDialogContent | null {
			if (!this.activeDomainClaimRestriction) return null;
			return getDomainClaimDialogContent({
				restriction: this.activeDomainClaimRestriction,
				teamDisplayLabel: this.displayLabels.team,
				ownerSelection: this.runnitState?.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? 'TEAM' : 'USER',
				isPaid: this.showStripeUserMembershipPortalBtn,
				isScim: Object.keys(this.user?.scim || {}).length > 0,
			});
		},
		// Mirrors the sidenav flyout's own v-if (RunnitSideNav.vue) so the modal can be its
		// exact complement — including the flyout's extra "active team" gate, which a hand-rolled
		// (smAndDown || !sidenav) complement missed (left a neither-shows gap on a sidenav route at
		// md+ with an inactive Team selected).
		domainClaimFlyoutWillShow (): boolean {
			return !!(
				this.activeDomainClaimRestriction &&
				this.$vuetify.breakpoint.mdAndUp &&
				isRouteRunnitSideNavPage(this.$route, this.user) &&
				(this.runnitState?.runnitsOwnerSelection !== RUNNITS_OWNER_SELECTION.TEAM || this.isTeamActive)
			);
		},
		// Fallback: show the modal whenever there's a restriction the sidenav flyout isn't showing.
		showDomainClaimModal (): boolean {
			return !!(
				this.domainClaimModalContent &&
				!this.domainClaimModalDismissed &&
				!this.domainClaimFlyoutWillShow
			);
		},
		// Re-arm key: changes whenever the restriction / selected account changes, so a
		// per-session dismiss doesn't permanently suppress the modal for a NEW restricted context.
		domainClaimContextKey (): string {
			const r = this.activeDomainClaimRestriction;
			if (!r) return '';
			return `${r.companyId}:${this.runnitState && this.runnitState.runnitsOwnerSelection}:${this.team && this.team.id}`;
		},
		loadingRouterViewByPermission () {
			if (!this.$route.meta.emailVerifiedAuthRequired) return false;
			if (this.loadingUser || this.loadingTeam) return true;
			return !!(
				(
					this.$route.meta.companyPermissionRequired &&
					this.loadingCompanyUserRole
				) ||
				(
					this.$route.meta.teamPermissionRequired &&
					this.loadingTeamUserRole
				)
			);
		},
		isLoadingBoardRoute () {
			// While the auth/permission gate is up, the board route hasn't mounted
			// yet — show the board skeleton in its place so users see one
			// continuous loading UI from URL hit through fully-rendered board.
			return this.$route.name === ROUTER.RUNNITS_BOARD;
		},
		hideRouterViewByPermission () {
			if (!this.$route.meta.emailVerifiedAuthRequired) return false;
			return !!(
				(
					this.$route.meta.companyPermissionRequired &&
					!this.hasCompanyUserRolePermission(this.$route.meta.companyPermissionRequired)
				) ||
				(
					this.$route.meta.teamPermissionRequired &&
					!this.hasTeamUserRolePermission(this.$route.meta.teamPermissionRequired)
				)
			);
		},
		showSessionTopToolbar () {
			return !!(!this.isLoginRequired && this.isSessionBootingOrLaunched);
		},
		isNotGoToPaymentRoute () {
			return this.$route.name !== 'GoToPayment';
		},
		// Full-screen apps (the canvas board) own the whole viewport: no promo or
		// onboarding dialogs over them.
		isFullscreenAppRoute () {
			return !!this._get(this.$route, 'meta.isFullscreenApp');
		},
		newUserSurveyShowing (): boolean {
			return !!((this.shouldNewUserOnboardingOpen && !this.skipNewUserSurvey) || this.showMqlCalendarDialog);
		},
		// A new account owes the Boards v2 tour from its survey until it's done
		// (IL-095): the app sends it there from any page but the few that must
		// stay reachable, once the survey (or the sales calendar) is closed.
		boardsTourDue (): boolean {
			return !!(this.user && !this.isAdminProxyingUser && boardsTourRequired(this.user));
		},
		boardsTourHere (): string {
			const route = this.$route;
			if (!route || !route.name) return 'wait';
			if (route.name === ROUTER.BOARDS_TOUR) return 'tour';
			if (route.meta && route.meta.isLoginPage) return 'exempt';
			return ['GoToPayment', 'UserNotActivated', 'ServerMaintenance'].includes(route.name) ? 'exempt' : 'app';
		},
		isAcceptingTeamInvite () {
			return !!(
				this.addingToTeam ||
				(
					this.loginQueryParamsData &&
					this.loginQueryParamsData.teamId &&
					this.loginQueryParamsData.inviteCode
				) ||
				this._get(this.$route, 'query.finishedConnectingTeam') === '1'
			);
		},
		newUserPromotionIsLive () {
			const startDate = new Date(NEW_USER_PROMO_START_DATE);
			const endDate = new Date(NEW_USER_PROMO_END_DATE);
			const currentTime = new Date();

			return !!(
				currentTime >= startDate &&
				currentTime <= endDate
			);
		},
		usingBraveBrowser () {
			// Check for Brave browser
			if (this._get(navigator, 'brave') && this._get(navigator, 'brave.isBrave.name') === 'isBrave') {
				return true;
			}

			// If none of the unsupported browsers were detected, return true
			return false;
		},
		allowFileBrowserSidePanel () {
			// Mobius sessions don't have an apps array; treat them as non-files-only (show the panel).
			const apps = this.toolbar.session && this.toolbar.session.apps;
			const isFilesOnly = !!(apps && apps.length && apps.every((app: AppKey) => app === APP_KEY.FILE_001));
			return !!(this.isSessionLaunched && !isFilesOnly);
		},
		shouldShowUnsupportedDialog () {
			return !!(
				this.isLoggedIn &&
				this.usingBraveBrowser
			);
		},
		shouldDisableRoundedCorners () {
			return !!this._get(this.user, 'runnitSettings.libraryRoundedCornersOff');
		},
		showAnnouncementsBanner () {
			return !!(
				this.user &&
				!this._get(this.$route, 'meta.isLoginPage') &&
				!this._get(this.$route, 'meta.hideAnnouncementsBanner')
			);
		},
		isLoginPage () {
			return !!this.$route.meta.isLoginPage;
		},
		isLoginRequired () {
			return !!(
				this.isLoginPage &&
				(
					!this.isLoggedIn ||
					this.$route.meta.forcedLoginRequired
				)
			);
		},
		isSessionBootingOrLaunched () {
			return !!(
				this.toolbar.session &&
				(
					READY_SESSION_STATES.includes(this.toolbar.session.state) ||
					BOOTING_SESSION_STATES.includes(this.toolbar.session.state)
				) &&
				this.$route.name === ROUTER.LAUNCH
			);
		},
		isSessionLaunched () {
			return !!(
				this.toolbar.session &&
				READY_SESSION_STATES.includes(this.toolbar.session.state) &&
				this.$route.name === ROUTER.LAUNCH
			);
		},
	},
	watch: {
		boardsTourDue () {
			this.maybeSendToBoardsTour();
		},
		boardsTourHere () {
			this.maybeSendToBoardsTour();
		},
		newUserSurveyShowing () {
			this.maybeSendToBoardsTour();
		},
		// Re-arm the centered domain-claim modal when the restricted context changes (e.g. the
		// caller switches to another still-restricted account), so a prior per-session dismiss
		// doesn't permanently suppress it.
		domainClaimContextKey(newKey: string, oldKey: string) {
			if (newKey && newKey !== oldKey) {
				this.domainClaimModalDismissed = false;
			}
		},
		announcements: {
			immediate: true,
			handler(newVal: Announcement[], oldVal: Announcement[]) {
				if (newVal !== oldVal) {
					this.resetAnnouncementsLocalStorage(this.user, newVal, this.loadingAnnouncements);
				}
			}
		},
		loadingAnnouncements: {
			immediate: true,
			handler(newVal: StoreLoadingState, oldVal: StoreLoadingState) {
				if (newVal !== oldVal) {
					this.resetAnnouncementsLocalStorage(this.user, this.announcements, newVal);
				}
			}
		},
		hideRouterViewByPermission: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal && !oldVal) {
					if (this.$route.name === ROUTER.SESSIONS) {
						const platformHomePreference: PlatformHomePreference = getPlatformHomePreference();
						// If preference is unset or RUNNIT, go to Runnit home
						if (!platformHomePreference || platformHomePreference === 'RUNNIT') {
							setPlatformHomePreference('RUNNIT');
							this.routerReplace(this.$route, this.$router, { name: ROUTER.RUNNITS_HOME });
						}
					}
				}
			},
		},
		'$route': {
			immediate: true,
			handler (newVal: Route, oldVal: Route) {
				if (!this.capturedSignInData && newVal.meta.emailVerifiedAuthRequired && newVal !== oldVal) {
					this.doDeviceReportLocal();
				}

				if (
					!this.$autoSoundMachine.isInitialized() &&
					!this.sessionSoundsOff &&
					newVal.name === ROUTER.LAUNCH &&
					(
						!oldVal ||
						oldVal.name !== ROUTER.LAUNCH
					)
				) {
					// Pop confirm dialog over all the iframes so that our $autoSoundMachine can get a click listener to init itself
					this.autoSoundMachineNeedsInit = true;
				}

				if (this._get(newVal, 'meta.pageColor')) {
					this.$vuetify.theme.themes.dark.background = newVal.meta.pageColor;
				} else {
					this.$vuetify.theme.themes.dark.background = '#171717';
				}

				this.initRunnitSideNavByRoute(newVal, this.user);
			},
		},
		'$store.state.triggers.screenResizeBreakpointTrigger': {
			immediate: true,
			handler (newVal: Breakpoint, oldVal: Breakpoint) {
				const oldSmAndDown: boolean = !!this._get(oldVal, 'smAndDown');
				const newSmAndDown: boolean = !!this._get(newVal, 'smAndDown');
				if (newSmAndDown !== oldSmAndDown) {
					const runnitsSideNavOpen: boolean = !!(!newSmAndDown && isRouteRunnitSideNavPage(this.$route, this.user));
					if (runnitsSideNavOpen !== this.toolbar.runnitsSideNavOpen) {
						this.updateToolbar({
							runnitsSideNavOpen,
						});
					}
				}
			},
		},
		isSessionLaunched (newVal, oldVal) {
			if (newVal !== oldVal && this.$vuetify.breakpoint.smAndUp) {
				this.updateToolbar({
					fileBrowserOpen: true,
					sessionBarOpen: true,
				});
			}

			if (!newVal) {
				this.updateToolbar({
					sessionBarOpen: false,
				});
			}
		},
		authUser (newVal: FirebaseUser, oldVal: FirebaseUser) {
			// Sign-out: drop this browser's Global Search history. Recents are
			// raw query text kept per user AND per account, so they outlive the
			// session and are readable by whoever signs in next on a shared
			// machine. Swept here rather than at each `auth.signOut()` call
			// site (there are several) because this watcher is the one place
			// every sign-out path converges on.
			if (oldVal && !newVal) {
				clearAllGlobalSearchRecents();
			}
			this.bindAuthUserDependentState(newVal, oldVal, this.user);
			if (shouldRedirectToEmailNotVerified(this.$route, newVal)) {
				this.routerReplace(this.$route, this.$router, {
					name: ROUTER.EMAIL_VERIFICATION,
					query: {
						...(this.$route.fullPath !== '/' && {
							redirect: this.$route.fullPath,
						}),
					},
				});
			}

			// Check if we should show onboarding dialog after email verification
			if (newVal && newVal.emailVerified && this.user) {
				this.maybeOpenNewUserOnboardingDialog(this.user);
				this.maybeTrackLinkedInFreeSignupConversion(this.user);
			}
		},
		team (newVal: Team, oldVal: Team) {
			this.onTeamChangeBinding(newVal, oldVal);
		},
		loginQueryParamsData: {
			immediate: true,
			async handler (newVal: LoginQueryParamsDataState, oldVal: LoginQueryParamsDataState) {
				if (this.user && newVal !== oldVal && newVal && newVal.teamId && newVal.inviteCode) {
					await this.connectTeamToTeamMemberSignup();
				}
			},
		},
		user: {
			immediate: true,
			async handler (newVal: User, oldVal: User) {
				const oldUserId: string = this._get(oldVal, 'id') || null;
				const newUserId: string = this._get(newVal, 'id') || null;

				// Reset announcements localStorage
				if (newVal !== oldVal) {
					this.resetAnnouncementsLocalStorage(newVal, this.announcements, this.loadingAnnouncements);
				}

				// Sign-out transition (had a user, now none). This is the catch-all
				// for every auth.signOut() call site outside logout(); see
				// resetAnalyticsIdentity for why the two callers both exist.
				if (oldUserId && !newUserId) {
					this.resetAnalyticsIdentity();
				}

				if (!newUserId) return;

				if (!this.capturedSignInData && this.$route.meta.emailVerifiedAuthRequired && oldUserId !== newUserId) {
					this.doDeviceReportLocal();
				}

				this.onUserChangeBinding(newVal, oldVal);
				if (shouldRedirectFromUserNotActivated(this.$route, newVal)) {
					this.routerReplace(this.$route, this.$router, {
						name: ROUTER.SESSIONS,
					});
				} else if (shouldRedirectToUserNotActivated(this.$route, newVal)) {
					this.routerReplace(this.$route, this.$router, {
						name: 'UserNotActivated',
					});
				}

				// If user data changes, identify them in Amplitude
				if (oldUserId !== newUserId || newVal.email !== oldVal.email) {
					this.identifyAmplitudeUser(newVal, this.team);
				}

				// loginQueryParamsData checks
				if (!oldUserId && newUserId && this.loginQueryParamsData && this.loginQueryParamsData.teamId && this.loginQueryParamsData.inviteCode) {
					await this.connectTeamToTeamMemberSignup();
				}

				if (!oldUserId && newUserId) {
					this.initRunnitSideNavByRoute(this.$route, newVal);
				}

				// Set GoogleAnalytics clientId, HubSpot tracking, Google Ads click IDs, and UTM attribution
				if (oldUserId !== newUserId) {
					// Re-hydrate attribution from the shared cookie at login time as a "second chance".
					// This covers cases where the app was already open (so App.vue created() didn't run)
					// when the marketing site set/updated rd_attribution_v1.
					hydrateAttributionFromSharedCookie();

					const gtmCookies = getGtmCookies(newUserId);
					const adsClickIds = getGoogleAdsClickIds();
					const utmState = persistUtmAttributionFromUrl();
					const sessionAttrs = getStoredSessionAttributes();

					const hasAnyAdsClickId = !!(adsClickIds?.gclid || adsClickIds?.gbraid || adsClickIds?.wbraid);
					const hasAnyUtm = !!(utmState.first.utm_source?.value || utmState.first.utm_medium?.value || utmState.first.utm_campaign?.value || utmState.last.utm_source?.value || utmState.last.utm_medium?.value || utmState.last.utm_campaign?.value);
					const hasAnySessionAttrs = !!sessionAttrs?.sessionAttributesEncoded;

					// HubSpot identify
					if (newVal.email) {
						const _hsq = window._hsq = window._hsq || [];
						_hsq.push(['identify', {
							email: newVal.email,
							id: newUserId,
						}]);
					}

					// Update GTM cookies including hubspotutk, Google Ads click IDs, and UTM attribution
					// Important: persist click IDs even if gtmCookies.clientId is missing (cookies/consent blocked)
					if (gtmCookies || hasAnyAdsClickId || hasAnyUtm || hasAnySessionAttrs) {
						const { clientId, sessionId, gclid, fbp, hubspotutk } = gtmCookies || {
							clientId: null,
							sessionId: null,
							gclid: null,
							fbp: null,
							hubspotutk: null,
						};

						const shouldUpdate =
							(clientId && newVal.gtm?.clientId !== clientId) ||
							(sessionId && newVal.gtm?.sessionId !== sessionId) ||
							(gclid && newVal.gtm?.gclid !== gclid) ||
							(fbp && newVal.gtm?.fbp !== fbp) ||
							(hubspotutk && newVal.gtm?.hubspotutk !== hubspotutk) ||
							(adsClickIds?.gclid && (newVal as any).gtm?.gclidRaw !== adsClickIds.gclid) ||
							(adsClickIds?.gbraid && (newVal as any).gtm?.gbraid !== adsClickIds.gbraid) ||
							(adsClickIds?.wbraid && (newVal as any).gtm?.wbraid !== adsClickIds.wbraid) ||
							(sessionAttrs?.sessionAttributesEncoded && (newVal as any).gtm?.sessionAttributesEncoded !== sessionAttrs.sessionAttributesEncoded) ||
							hasAnyUtm; // Always update if we have UTM data (they may have changed)

						if (shouldUpdate) {
							const userRef = db.doc(`users/${newUserId}`);
							await userRef.update({
								gtm: {
									...(newVal.gtm || {}),
									...(clientId && { clientId }),
									...(sessionId && { sessionId }),
									...(gclid && { gclid }),
									...(fbp && { fbp }),
									hubspotutk: hubspotutk || null,
									// Google Ads click IDs for offline conversion tracking
									...(adsClickIds?.gclid && { gclidRaw: adsClickIds.gclid }),
									...(adsClickIds?.gbraid && { gbraid: adsClickIds.gbraid }),
									...(adsClickIds?.wbraid && { wbraid: adsClickIds.wbraid }),
									...(adsClickIds?.capturedAtMs && { gclidCapturedAtMs: adsClickIds.capturedAtMs }),
									// Google Ads session attributes
									...(sessionAttrs?.sessionAttributesEncoded && { sessionAttributesEncoded: sessionAttrs.sessionAttributesEncoded }),
									...(sessionAttrs?.capturedAtMs && { sessionAttributesCapturedAtMs: sessionAttrs.capturedAtMs }),
									// UTM attribution (First Touch + Last Touch)
									utmFirst: utmState.first,
									utmLast: utmState.last,
								},
							});
						}

						// Submit HubSpot signup conversion (deduped on backend)
						if (newVal.email) {
							try {
								const submitHubspotConversionRef = functions.httpsCallable('submitHubspotConversion');
								await submitHubspotConversionRef({
									eventName: 'FreeAccountSignup',
									pageName: 'FreeAccountSignup',
									pageUri: window.location.href,
									email: newVal.email,
									userId: newUserId,
								});
							} catch (e) {
								// Non-blocking: log error but don't interrupt user flow
								console.error('Error submitting HubSpot signup conversion:', e);
							}
						}
					}
				}

				// Set New User Promotion
				if ((newVal.trial && this.newUserPromotionIsLive)) {
					this.shouldNewUserPromoOpen = true;
				}

				// Check and open new user onboarding dialog
				await this.maybeOpenNewUserOnboardingDialog(newVal);
				this.maybeTrackLinkedInFreeSignupConversion(newVal);
			},
		},
		countDownTimerVal (newVal: string, oldVal: string) {
			if (
				newVal &&
				newVal !== oldVal &&
				this.toolbar &&
				this.toolbar.session &&
				READY_SESSION_STATES.includes(this.toolbar.session.state)
			) {
				if (
					newVal === this.TEN_MIN_TIME_VAL &&
					this.toolbar.session.scheduledStopMins !== 10
				) {
					this.timeExpiringDialog = {
						open: true,
						minutesLeft: 10,
					};
				} else if (newVal === this.TWO_MIN_TIME_VAL) {
					this.timeExpiringDialog = {
						open: true,
						minutesLeft: 2,
					};
				} else if (newVal === this.THIRTY_SEC_TIME_VAL) {
					this.$autoSoundMachine.playSessionEndsInSeconds(false, 30000);
				}
			}
		},
		stableDiffusionModels (newVal: StableDiffusionModel[]) {
			if (newVal && newVal.length) {
				const modelVersionId: string = getCivitaiModelVersionId();
				if (modelVersionId) {
					const foundModel: StableDiffusionModel = newVal
						.find((sdm: StableDiffusionModel) => sdm.modelVersionId === modelVersionId) || null;
					this.setStableDiffusionModel(foundModel);
				}
			}
		},
	},
	methods: {
		onGlobalSearchKeydown (event: KeyboardEvent) {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && !event.defaultPrevented) {
				if (!this.user?.isAdmin) return;
				event.preventDefault();
				this.updateRunnitState({
					globalSearchOpen: !this.runnitState.globalSearchOpen,
					globalSearchTrigger: 'shortcut',
				});
			}
		},
		...mapActions([
			'updateRunnitState',
			'tickRealTimeMoment',
			'setAdminUserProxyTokens',
			'updateLoader',
			'bindUser',
			'bindUserBalanceAccount',
			'bindStripeCustomer',
			'bindCurrSessions',
			'bindCompanyUserRole',
			'unbindCompanyUserRole',
			'bindTeamUserRole',
			'unbindTeamUserRole',
			'bindAppOffers',
			'bindHardwareOffers',
			'bindSoftwareOffers',
			'bindDeploymentImages',
			'bindStableDiffusionModels',
			'loadCivitaiPreloadModel',
			'setStableDiffusionModel',
			'setAuthUser',
			'updateLoginQueryParamsData',
			'setDeviceReportLocal',
			'updateSnackbar',
			'updateToolbar',
			'closeAllDrawers',
			'updateScreenResizeBreakpointTrigger',
			'bindAllTeamData',
			'unbindAllTeamData',
			'bindAnnouncements',
			'setShowMqlCalendarDialog',
		]),
		onOnboardingDialogInput (val: boolean) {
			if (val) return;
			this.shouldNewUserOnboardingOpen = false;
			this.setShowMqlCalendarDialog(false);
		},
		// The survey is in (IL-095): with new accounts switched into Boards v2, the
		// account owes the tour, and goes straight there (after the sales
		// calendar, for a lead); the product's pages come after it.
		async onNewUserSurveyCompleted (done: { isMql: boolean }) {
			if (!this.boardsV2NewAccounts || !this.user || !this.user.id) return;
			const next = nextBoardsTourState(boardsTourState(this.user), 'due', { required: true, newAccount: true });
			try {
				await db.doc(`users/${this.user.id}`).set({
					featureToursCompletedMap: { [BOARDS_TOUR_ID]: { ...next, updatedAt: FieldValue.serverTimestamp() } },
				}, { merge: true });
			} catch (e) {
				// on into the app as before, rather than a survey that never closes
				console.error('[tour] could not mark the tour as owed', e);
				if (!done.isMql) this.shouldNewUserOnboardingOpen = false;
				return;
			}
			rememberBoardsVersion('v2');
			if (done.isMql) return;
			this.shouldNewUserOnboardingOpen = false;
			this.$router.replace({ name: ROUTER.BOARDS_TOUR }).catch(() => {});
		},
		maybeSendToBoardsTour () {
			if (!this.boardsTourDue || this.newUserSurveyShowing || this.boardsTourHere !== 'app') return;
			// the tour couldn't load, and they went on for now (this tab only)
			if (boardsTourSnoozed()) return;
			this.$router.replace({ name: ROUTER.BOARDS_TOUR }).catch(() => {});
		},
		resetAnnouncementsLocalStorage(user: User, announcements: Announcement[], loadingAnnouncements: StoreLoadingState) {
			if (
				user &&
				loadingAnnouncements === false &&
				(
					_isEmpty(announcements) ||
					announcements.every((announcement: Announcement) => announcement.expiresAt && announcement.expiresAt.toDate() < new Date())
				)
			) {
				removeAnnouncementDismissedAll(user.id);
			}
		},
		isRouteRunnitSideNavPage,
		isRouteRunnitBoardPage,
		initSecondInterval () {
			if (this.secondInterval) {
				clearInterval(this.secondInterval);
			}
			this.secondInterval = setInterval(() => {
				this.tickRealTimeMoment();
			}, 1000);
		},
		removeBadAmplitudeCookie () {
			// In browser JavaScript, you can't directly check a cookie's domain
			// The most reliable approach is to simply attempt to delete all AMP_ cookies
			// from the specific domain we want to remove
			const cookies = document.cookie.split(';')
			cookies.forEach(cookie => {
				const [name] = cookie.trim().split('=')
				if (name.startsWith('AMP_')) {
					// Explicitly target cookies from app.rundiffusion.com domain
					// This won't affect cookies from .rundiffusion.com
					document.cookie = `${name}=; domain=app.rundiffusion.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
				}
			})
		},
		initializeAmplitude (user: User, team: Team) {
			// Only initialize once to prevent duplicate sessions
			if (this.isAdminProxyingUser) {
				return;
			}

			try {
				const apiKey = 'af12e229fc377b4e2d871b25ee1e2fe9';

				// Initialize with proper configuration
				if (
					user?.id &&
					// Bring back the following commented out code, when we want fewer Amplitude MTUs (recordings)
					// (
					// 	(
					// 		user?.club &&
					// 		!user.club.startsWith('FREE-')
					// 	) ||
					// 	team?.isActive
					// ) &&
					!isAmplitudeInitializedWithUserId
				) {
					amplitude.add(engagementPlugin());
					amplitude.add(sessionReplayPlugin());
					amplitude.init(apiKey, user.id, {
						autocapture: {
							elementInteractions: true,
						},
						domain: '.rundiffusion.com', // used to share across subdomains
						sessionTimeout: 30 * 60 * 1000, // 30 minutes
						trackingOptions: {
							ipAddress: true,
							language: true,
							platform: true,
						},
					} as any);
					isAmplitudeInitializedWithUserId = true;
				}
			} catch (error) {
				console.error('Error initializing Amplitude:', error);
			}
		},
		identifyAmplitudeUser (user: User, team: Team) {
			if (this.isAdminProxyingUser || !user || !user.email) {
				return;
			}

			this.initializeAmplitude(user, team);

			try {
				amplitude.setUserId(user.id);
				const identify = new amplitude.Identify();

				// Additional user properties
				if (user.email) identify.set('email', user.email);
				if (user.createdAt) identify.set('createdAt', user.createdAt.toDate().toISOString());
				if (user.club) identify.set('club', user.club);
				if (user.trial) identify.set('isRDTrial', !!user.trial);
				if (user.isAdmin) identify.set('isAdmin', !!user.isAdmin);
				if (user.teamIds) identify.set('connectedTeams', Object.keys(user.teamIds || {}).join(','));

				amplitude.identify(identify);
				// console.log('User identified in Amplitude:', user.email);
			} catch (error) {
				console.error('Failed to identify user in Amplitude:', error);
			}

			this.identifyAnalyticsUser(user, team);
		},
		// Tears down the analytics identity on sign-out. posthog-js PERSISTS the
		// distinct_id and super-properties, so without this the next person to
		// sign in on a shared browser inherits the previous user's identity and
		// super-props until the next register() (ADR-0016 gap, closed here).
		//
		// Clearing the stored identify fingerprint is not optional alongside the
		// reset: with person_profiles: 'identified_only', a reset session that
		// then SKIPS identify (because the fingerprint still matched) would stay
		// on a fresh anonymous distinct_id for the rest of the session. Reset and
		// fingerprint-clear must always happen together, which is why they live
		// in one method rather than at each call site.
		//
		// Called from two places on purpose: logout() calls it explicitly because
		// that path races a window.location.reload() and the watcher may never
		// get a turn; the user watcher covers the other six auth.signOut() call
		// sites (AuthDevices, AdminUsersManagement, DomainClaimResolutionContent,
		// UserNotActivated, onExitProxy). Idempotent, so double-calling is fine.
		resetAnalyticsIdentity () {
			analytics.reset();
			removeAnalyticsIdentityFingerprint();
			this.lastAnalyticsIdentityFingerprint = null;
		},
		identifyAnalyticsUser (user: User, team: Team) {
			// Mirrors the isAdminProxyingUser gate in identifyAmplitudeUser so
			// admin-proxy sessions don't pollute product-analytics data.
			if (this.isAdminProxyingUser || !user || !user.email) {
				return;
			}

			try {
				// identify() carries PERSON-level props only; the super-props
				// (surface/is_team_user/team_id/...) go through register() so they never
				// pollute the person. Both are assembled by the shared helpers (ADR-0016),
				// keyed by SUPER_PROPS so this surface can't drift from the plugins.
				const accountAgeDays: number | null = accountAgeDaysFromMs(
					user.createdAt ? user.createdAt.toDate().getTime() : null,
					Date.now(),
				);
				const plan = user.club || null;
				const isInternalAdmin = !!user.isAdmin;

				// identify() emits a $set event every time it is called, and it was
				// firing about once per page load: ~53k $set a month, 7% of all
				// PostHog volume, mostly re-asserting person properties that had
				// not changed. The fingerprint is persisted per browser (not just
				// held in component data) precisely because the duplication is
				// ACROSS page loads, not within one. account_age_days is part of
				// the fingerprint, so a returning user still re-identifies once a
				// day and person properties never go stale by more than that.
				// register() below is NOT deduped: it is local SDK state, sends
				// nothing, and must stay authoritative for the team super-props.
				const personProps = buildPersonProps({
					email: user.email,
					plan,
					isRdTrial: !!user.trial,
					isInternalAdmin,
					accountAgeDays,
				});
				// In-memory value is the same-session guard; the stored value carries
				// the dedupe across loads. When storage is unavailable (incognito,
				// blocked) getAnalyticsIdentityFingerprint returns null and we simply
				// identify as before — degrading to the old behaviour rather than
				// dropping identity.
				const fingerprint: string = identityFingerprint(user.id, personProps);
				if (shouldIdentify(
					fingerprint,
					this.lastAnalyticsIdentityFingerprint,
					getAnalyticsIdentityFingerprint(),
				)) {
					analytics.identify(user.id, personProps);
					// Only record the identify once it actually reached a live
					// provider. posthogAnalytics.identify() swallows its errors, so
					// persisting first would bank a dropped identify as a success and
					// suppress every retry until account_age_days rolls over (up to a
					// day). When the SDK never loaded, we leave no fingerprint and try
					// again on the next load.
					if (analytics.isReady()) {
						this.lastAnalyticsIdentityFingerprint = fingerprint;
						setAnalyticsIdentityFingerprint(fingerprint);
					}
				}

				analytics.register(buildSuperProps({
					surface: SURFACES.WEB,
					plan,
					isTeamUser: !!(team && team.isActive),
					teamId: (team && team.id) || null,
					accountAgeDays,
					isInternalAdmin,
				}));
			} catch (error) {
				console.error('Failed to identify user in analytics:', error);
			}
		},
		trackEvent (eventName: string, eventProperties?: Record<string, any>) {
			if (!isAmplitudeInitialized || this.isAdminProxyingUser) {
				return;
			}

			try {
				// Create a unique key for this event in this session
				const eventKey = `${eventName}_${JSON.stringify(eventProperties || {})}`;

				// Check if we've already tracked this event in this session
				if (!sessionStorage.getItem(eventKey)) {
					amplitude.track(eventName, eventProperties);

					// Store that we've tracked this event
					sessionStorage.setItem(eventKey, 'true');

				}
			} catch (error) {
				console.error(`Error tracking event ${eventName}:`, error);
			}
		},
		bindAuthUserDependentState (newAuthUser: FirebaseUser, oldAuthUser: FirebaseUser, user: User) {
			const newUid: string = (newAuthUser && newAuthUser.uid) || null;
			const oldUid: string = (oldAuthUser && oldAuthUser.uid) || null;
			if (
				newUid &&
				(
					newUid !== oldUid ||
					!user
				)
			) {
				this.bindUser(newAuthUser);
			}
		},
		onTeamChangeBinding (newTeam: Team, oldTeam: Team) {
			const newId: string = (newTeam && newTeam.id) || null;
			const oldId: string = (oldTeam && oldTeam.id) || null;

			if (
				newId &&
				newId !== oldId
			) {
				// Visibility depends on team sometimes, so re-bind on team change
				this.bindAppOffers(this.user);
				this.bindHardwareOffers(this.user);
				this.bindSoftwareOffers(this.user);
				this.bindDeploymentImages(this.user);

				// Bind company and team user roles
				if (this.user) {
					if (newTeam.companyId) {
						this.bindCompanyUserRole({ userId: this.user.id, companyId: newTeam.companyId });
					} else {
						this.unbindCompanyUserRole();
					}
					this.bindTeamUserRole({ userId: this.user.id, teamId: newTeam.id });
				} else {
					this.unbindCompanyUserRole();
					this.unbindTeamUserRole();
				}

				// Microsoft Clarity
				const { clarity } = (window as any);
				if (clarity) {
					clarity('set', 'teamId', newTeam.id);
					clarity('set', 'teamName', newTeam.name);
					clarity('set', 'teamRegion', newTeam.region);
					clarity('set', 'teamIsActive', newTeam.isActive ? 'Yes' : 'No');
				}

				// Track team change in Amplitude
				this.initializeAmplitude(this.user, newTeam);
				if (isAmplitudeInitialized && !this.isAdminProxyingUser) {
					this.trackEvent('team_selected', {
						teamId: newTeam.id,
						teamName: newTeam.name,
					});
				}
			}
		},
		onUserChangeBinding (newUser: User, oldUser: User) {
			const newId: string = (newUser && newUser.id) || null;
			const oldId: string = (oldUser && oldUser.id) || null;

			if (
				newId &&
				newId !== oldId
			) {
				this.bindUserBalanceAccount(newUser);
				this.bindStripeCustomer(newUser);
				this.bindCurrSessions(newUser);
				this.bindAppOffers(newUser);
				this.bindHardwareOffers(newUser);
				this.bindSoftwareOffers(newUser);
				this.bindDeploymentImages(newUser);

				// Microsoft Clarity
				const { clarity } = (window as any);
				if (clarity) {
					clarity('identify', newUser.email, newUser.id);
					clarity('set', 'createdAt', moment(newUser.createdAt.toMillis()).toISOString());
					clarity('set', 'creatorsClub', newUser.club && newUser.club.startsWith('CC-') ? 'Yes' : 'No');
					clarity('set', 'club', newUser.club);
					clarity('set', 'discordUsername', newUser.discordUser ? newUser.discordUser.username : null);
					clarity('set', 'region', newUser.region);
					clarity('set', 'storageUuid', newUser.uuid);
					clarity('set', 'isTrial', newUser.trial ? 'Yes' : 'No');
					clarity('set', 'isAdmin', newUser.isAdmin);
					clarity('set', 'testingNewTagAdded', true);
					clarity('set', 'numTeams', _keys(newUser.teamIds || {}).length);
				}
			}
			if (newId) {
				this.handleTeamBindingFromUserChange(newUser);
			}
		},
		handleTeamBindingFromUserChange (user: User) {
			const lastSelectedTeamId: string = getTeamId();
			const hasOnlyOneTeam: boolean = _keys(user.teamIds || {}).length === 1;

			if (
				user &&
				hasOnlyOneTeam
			) {
				if (!this.runnitState.runnitsOwnerSelection) {
					const runnitsOwnerSelection: RunnitsOwnerSelection = RUNNITS_OWNER_SELECTION.TEAM;
					this.switchBetweenTeamAndUser(runnitsOwnerSelection);
				}
				const oneTeamId: string = _keys(user.teamIds)[0];
				if (this.team && this.team.id === oneTeamId) {
					return; // Leave the Team binding as it is
				}

				this.bindAllTeamData(oneTeamId);
			} else if (
				user &&
				!_isEmpty(user.teamIds)
			) {
				if (!this.runnitState.runnitsOwnerSelection) {
					const runnitsOwnerSelection: RunnitsOwnerSelection = RUNNITS_OWNER_SELECTION.TEAM;
					this.switchBetweenTeamAndUser(runnitsOwnerSelection);
				}
				if (lastSelectedTeamId && user.teamIds[lastSelectedTeamId]) {
					if (this.team && this.team.id === lastSelectedTeamId) {
						return; // Leave the Team binding as it is
					}

					this.bindAllTeamData(lastSelectedTeamId);
				} else {
					const randomTeamId = Object.keys(user.teamIds)[0];
					this.bindAllTeamData(randomTeamId);
				}
			} else {
				// A user with no team memberships must never be left in TEAM owner
				// selection. runnitsOwnerSelection is always seeded to 'TEAM' or 'USER'
				// at store init (never empty), so the old `if (!...runnitsOwnerSelection)`
				// guard never fired — a stale 'TEAM' in localStorage (prior membership,
				// admin proxy, shared browser) stuck the teamless user in TEAM mode and
				// the session/storage UI then dereferenced a null `team`. Force USER.
				if (this.runnitState.runnitsOwnerSelection !== RUNNITS_OWNER_SELECTION.USER) {
					this.switchBetweenTeamAndUser(RUNNITS_OWNER_SELECTION.USER);
				}
				this.unbindAllTeamData();
				this.unbindCompanyUserRole();
				this.unbindTeamUserRole();
			}
		},
		initRunnitSideNavByRoute (route: Route, user: User) {
			if (user) {
				if (isRouteRunnitSideNavPage(route, user) && this.$vuetify.breakpoint.mdAndUp) {
					this.updateToolbar({
						runnitsSideNavOpen: true,
					});
				} else {
					this.updateToolbar({
						runnitsSideNavOpen: false,
					});
				}
			}
		},
		async onExitProxy () {
			try {
				if (this.isAdminProxyingUser) {
					const proxyAdminToken: string = getProxyAdminToken();
					if (!proxyAdminToken) return;

					this.setAdminUserProxyTokens({ proxyAdminToken: null, proxyAdminIdToken: null });
					await auth.signOut();
					const userCredential: FirebaseUserCredential = await auth.signInWithCustomToken(proxyAdminToken);

					const routeData = this.$router.resolve({ name: 'AdminUsersManagement' });
					window.open(routeData.href, '_self');
				}
			} catch (e) {
				console.error(e);
				this.logout(false);
			}
		},
		async connectTeamToTeamMemberSignup () {
			if (this.addingToTeam) return;
			// The server accepts an invite only for the caller's own verified email. A new
			// email/password invitee keeps the invite context until they verify, and
			// runDeviceReportLocal retries it on the first verified page.
			if (!this.authUser || !this.authUser.emailVerified) return;
			const onError: Function = (e, message?: string) => {
				console.error('Error finishing connecting the team member to the team: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: message || 'Problem accepting your invitation, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
					timeout: 30000,
				});
			};

			let keepInviteContext: boolean = false;
			try {
				this.addingToTeam = true;
				this.updateLoader({
					show: true,
					message: 'Accepting your invitation, please wait...',
				});
				const teamId: string = this._get(this.loginQueryParamsData, 'teamId', null);
				const inviteCode: string = this._get(this.loginQueryParamsData, 'inviteCode', null);
				const functionRef = functions
					.httpsCallable('finishTeamMemberSignup');
				const { success, teamName, reason } = (await functionRef({
					teamId,
					inviteCode,
				})).data;

				if (reason === 'EMAIL_NOT_VERIFIED') {
					// The token predates verification; the retry after verification will carry it.
					keepInviteContext = true;
				} else if (!success) {
					onError(
						new Error(`finishTeamMemberSignup did not return success (${reason || 'no reason'})`),
						reason === 'ALREADY_ACCEPTED'
							? 'This invitation was already accepted by a different account. Please reach out for help by going to Resources -> Help from the menu options'
							: null,
					);
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: 'Success! Invitation accepted.',
						show: true,
					});
					// A new membership: the team tables and the dashboard cards catch up once the mirror has it
					noteTeamsAdded();

					// Update Vuex
					this.bindAllTeamData(teamId);

					// After they create new team, set variables to auto-select new team in pickers
					const runnitsOwnerSelection: RunnitsOwnerSelection = RUNNITS_OWNER_SELECTION.TEAM;
					this.switchBetweenTeamAndUser(runnitsOwnerSelection);


					// Track team invitation acceptance in Amplitude
					if (isAmplitudeInitialized && !this.isAdminProxyingUser) {
						this.trackEvent('team_invitation_accepted', {
							teamId,
							teamName,
						});
					}
				}
			} catch (e) {
				onError(e);
			} finally {
				this.addingToTeam = false;
				this.updateLoader({
					show: false,
				});
				if (!keepInviteContext) {
					this.updateLoginQueryParamsData({
						teamId: null,
						inviteCode: null,
					});
				}
			}
		},
		async runMembershipReconciliation () {
			// Membership Reconciliation (ADR 0018): after login, bind any pending team
			// invites / SCIM provisions by verified email, then auto-enter the one obvious
			// team or offer a picker. Membership is established server-side; this only
			// chooses the active Account Selection.
			if (this.isAdminProxyingUser) return;
			if (!this.user || !this.user.email) return;
			try {
				const functionRef = functions.httpsCallable('reconcilePendingTeamMemberships');
				const { reconciledTeams } = (await functionRef({})).data || {};
				const teams = Array.isArray(reconciledTeams) ? reconciledTeams : [];
				if (!teams.length) return;
				// New memberships: the team tables and the dashboard cards catch up once the mirror has them
				noteTeamsAdded();

				const reconciledTeamIds = new Set(teams.map((t: { teamId: string }) => t.teamId));
				// "Other teams" = teams the Caller already belonged to BEFORE this reconcile,
				// excluding the just-bound ones. Capture the teamIds map once (it's a reactive
				// Vuex value) so a mid-evaluation listener update can't make .some() inconsistent,
				// and count only explicit `=== true` memberships. Auto-enter (zero-click) only when
				// there are demonstrably none; otherwise decideReconcileEntry shows the picker.
				const teamIds: Record<string, boolean> = this.user.teamIds || {};
				const hasOtherTeams: boolean = _keys(teamIds)
					.some((id: string) => teamIds[id] === true && !reconciledTeamIds.has(id));

				const decision = decideReconcileEntry(teams, hasOtherTeams);
				if (decision.action === 'enter') {
					this.enterReconciledTeam(decision.teamId, decision.teamName);
				} else if (decision.action === 'picker') {
					this.membershipReconcileTeams = decision.teams;
					this.membershipReconcileDialogOpen = true;
				}
			} catch (e) {
				console.error('Membership reconciliation failed', e);
			}
		},
		enterReconciledTeam (teamId: string, teamName: string) {
			this.bindAllTeamData(teamId);
			this.switchBetweenTeamAndUser(RUNNITS_OWNER_SELECTION.TEAM);
			this.updateSnackbar({
				status: SNACKBAR_STATUS.SUCCESS,
				message: teamName ? `You've joined ${teamName}.` : `You've been added.`,
				show: true,
			});
			if (isAmplitudeInitialized && !this.isAdminProxyingUser) {
				this.trackEvent('membership_reconciled_enter', { teamId });
			}
		},
		onMembershipReconcileTeamSelect (team: { teamId: string; teamName: string }) {
			this.membershipReconcileDialogOpen = false;
			this.enterReconciledTeam(team.teamId, team.teamName);
		},
		closeMembershipReconcileDialog () {
			this.membershipReconcileDialogOpen = false;
		},
		onCloseAllDrawers () {
			this.closeAllDrawers();
			this.menuResetTrigger = this.menuResetTrigger + 1;
		},
		closeLogoutDialog () {
			this.updateToolbar({
				logoutDialogOpen: false,
				needReauthenticateDialogOpen: false,
			});
		},
		async logout (doLocationReload: boolean) {
			this.loggingOut = true;
			try {
				// Track logout event in Amplitude
				if (isAmplitudeInitialized && !this.isAdminProxyingUser) {
					this.trackEvent('user_logout');
				}

				// Before signOut, because this path reloads the window immediately
				// afterwards and the user watcher may never get a turn.
				this.resetAnalyticsIdentity();

				this.setAdminUserProxyTokens({ proxyAdminToken: null, proxyAdminIdToken: null });
				await auth.signOut();

				// Hard reload the window.location, this will clear & unbind all vuex state
				if (doLocationReload) {
					console.log('Reloading window: 3'); // TODO remove after bug fixed
					window.location.reload();
				} else {
					console.log('Reloading window: 4'); // TODO remove after bug fixed
					window.location.assign(`${window.location.origin}/login?${encodeURIComponent(this.$route.fullPath)}`);
				}

				// Don't set this.loggingOut to true so the button can remain in the loading state
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Uh oh! Error logging out, try again.',
					show: true,
				});
				this.loggingOut = false;
			}
		},
		doDeviceReportLocal () {
			// Memoize so callers (e.g. the onboarding gate) can await the same in-flight capture
			// and read the resulting sign-in flags (skipNewUserSurvey) without racing or re-calling.
			if (this.isAdminProxyingUser) return Promise.resolve();
			if (!this.deviceReportLocalPromise) {
				this.capturedSignInData = true;
				this.deviceReportLocalPromise = this.runDeviceReportLocal();
			}
			return this.deviceReportLocalPromise;
		},
		async runDeviceReportLocal () {
			try {
				// Time zone
				const timeZone: string = moment.tz.guess();

				// Fingerprint JS
				let fingerprint: Fingerprint = null;
				try {
					const fingerprintAgent = await FingerprintJs.load();
					fingerprint = await fingerprintAgent.get() || null;
					if (fingerprint) {
						fingerprint = {
							// Strip off "components" field, because firebase wouldn't let us save it in the db
							visitorId: fingerprint.visitorId,
							confidence: fingerprint.confidence,
							version: fingerprint.version,
						};
					}
				} catch (e) {
					console.error(e);
				}

				// Get referral code from browser storage if available
				const referredByCode: string = getAcceptedReferralCode() || null;

				// Capture sign in data
				let ip: string = null;
				let loginLastAtIso: string = null;
				// Preserve any previously-resolved restriction so a transient sign-in failure
				// never silently clears an active domain-claim restriction (false all-clear).
				let domainClaimRestriction: {
					companyId: string;
					companyName: string;
					normalizedDomain: string;
					matchingTeams?: { teamId: string; teamName: string }[];
				} | null = this.deviceReportLocal?.domainClaimRestriction ?? null;
				let skipNewUserSurvey: boolean = false;
				try {
					const functionRef = functions
						.httpsCallable('captureSignInData');
					const {
						success,
						ip: _ip,
						loginLastAtIso: _loginLastAtIso,
						domainClaimRestriction: restrictionFromServer,
						skipNewUserSurvey: skipNewUserSurveyFromServer,
					} = (await functionRef({
						timeZone,
						fingerprint,
						referredByCode,
						// Human Check Pass from the login page; the server needs it to grant a new account's trial
						humanPassId: getHumanCheckPassIdForSignIn(),
					})).data;

					// Only overwrite on a successful response — the server is authoritative when it answers.
					domainClaimRestriction = restrictionFromServer || null;
					skipNewUserSurvey = !!skipNewUserSurveyFromServer;

					if (success && _ip) {
						ip = _ip;
						loginLastAtIso = _loginLastAtIso;
					}
					if (success) {
						// Single use either way: spent on this account's trial, or not needed by an existing account
						clearHumanCheckPass();
					}
				} catch (e) {
					console.error(e);
				}

				// Set to store
				this.setDeviceReportLocal({
					timeZone,
					fingerprint,
					ip,
					loginLastAtMoment: loginLastAtIso ? moment(loginLastAtIso) : null,
					domainClaimRestriction,
					skipNewUserSurvey,
				});

				// An invite link opened before the email was verified waits until now: the server
				// accepts invites only for a verified email. Run it before reconciliation so the
				// two never bind the same invite at once.
				if (this.loginQueryParamsData && this.loginQueryParamsData.teamId && this.loginQueryParamsData.inviteCode) {
					await this.connectTeamToTeamMemberSignup();
				}

				// Membership Reconciliation (ADR 0018) — bind pending team invites / SCIM
				// provisions by verified email, then auto-enter or offer a picker. Inherits
				// the isAdminProxyingUser guard at the top of this method.
				await this.runMembershipReconciliation();
			} catch (e) {
				console.error(e);
			}
		},
		closeAccountMenu () {
			this.updateToolbar({
				accountMenuOpen: false,
			});
		},
		onResourcesDialogClose () {
			this.updateToolbar({
				resourcesDialog: {
					open: false,
					appOffer: null,
				},
			});
		},
		openRunnitSideNav () {
			this.updateToolbar({
				runnitsSideNavOpen: true,
			});
		},
		async maybeOpenNewUserOnboardingDialog (user: User) {
			if (this.hasCheckedNewUserOnboarding) return;
			if (!user || !user.id || !_isEmpty(user.teamIds)) return;

			// Wait until email is verified before showing onboarding
			if (!this.authUser || !this.authUser.emailVerified) {
				return;
			}

			// Never show onboarding while proxying as a user.
			if (this.isAdminProxyingUser) {
				return;
			}

			// Mark as checked immediately to prevent race conditions during the async Firestore query
			this.hasCheckedNewUserOnboarding = true;

			// Never show for team-invite acceptance flows (enterprise invites come through here).
			if (this.isAcceptingTeamInvite) {
				return;
			}

			// Only show for brand new users (prevents existing userbase from seeing this new survey key).
			const createdAt = this._get(user, 'createdAt', null);
			const createdAtMs: number = createdAt && createdAt.toMillis ? createdAt.toMillis() : null;
			const maxAgeMs = 1000 * 60 * 60 * 24 * 2; // 48 hours

			if (!createdAtMs || (Date.now() - createdAtMs) > maxAgeMs) {
				return;
			}

			// If user is already marked as MQL, skip auto-opening onboarding.
			// Side-nav re-open still works through showMqlCalendarDialog state.
			if (this._get(user, 'gtm.isMql') === true) {
				return;
			}

			// Skip the forced survey for enterprise customers. Primary signal is the data-driven
			// skipNewUserSurvey flag (verified domain claim of an Enterprise-tier company, computed
			// server-side in captureSignInData) — await the in-flight sign-in capture so it's
			// populated before deciding. The fallback domain list (enterpriseFallbackDomains) is an
			// additive client-side shim for customers not yet covered by a verified domain claim.
			try {
				await this.doDeviceReportLocal();
			} catch (e) {
				console.error(e);
			}
			if (this.skipNewUserSurvey || emailMatchesFallbackEnterpriseDomain(user.email)) {
				return;
			}

			try {
				// We specifically check for COMPLETED surveys. If ANY exist, we don't show the onboarding.
				// This handles cases where multiple docs might exist (due to race conditions in creation).
				const snap = await db
					.collection('surveys')
					.where('userId', '==', user.id)
					.where('key', '==', SURVEY_KEY.NEW_USER_ONBOARDING_1)
					.where('completed', '==', true)
					.limit(1)
					.get();

				if (snap.empty) {
					// whether the Boards v2 tour follows the survey, known before it can be submitted
					await this.readBoardsV2NewAccounts();
					this.shouldNewUserOnboardingOpen = true;
				}
			} catch (e) {
				console.error('Error checking onboarding survey:', e);
				// Fail closed: if we can't verify completion status, don't show onboarding.
				this.shouldNewUserOnboardingOpen = false;
			}
		},
		async readBoardsV2NewAccounts () {
			try {
				const snap = await db.doc('masterConfigs/boardsV2').get();
				this.boardsV2NewAccounts = !!(snap.exists && snap.data() && snap.data().newAccounts === true);
			} catch (e) {
				// unreadable: the survey ends as it always has
				console.error('[tour] could not read the Boards v2 switch', e);
				this.boardsV2NewAccounts = false;
			}
		},
		maybeTrackLinkedInFreeSignupConversion (user: User) {
			if (!user || !user.id) return;

			// Only fire once the account is confirmed via email verification.
			// SSO sign-ups are pre-verified, so this also covers them.
			if (!this.authUser || !this.authUser.emailVerified) return;

			// Never count admins proxying as another user.
			if (this.isAdminProxyingUser) return;

			// Only brand new accounts — prevents the existing userbase from firing the
			// conversion on their next login. Matches the new-user onboarding window.
			const createdAt = this._get(user, 'createdAt', null);
			const createdAtMs: number = createdAt && createdAt.toMillis ? createdAt.toMillis() : null;
			const maxAgeMs = 1000 * 60 * 60 * 24 * 2; // 48 hours
			if (!createdAtMs || (Date.now() - createdAtMs) > maxAgeMs) return;

			// Fires at most once per user (deduped in localStorage; production-only,
			// where the LinkedIn Insight Tag is loaded via GTM).
			trackLinkedInFreeSignupConversion(user.id);
		},
	},
	components: {
		GlobalSearchModal: () => import(/* webpackChunkName: "global-search" */ '@/components/GlobalSearch/GlobalSearchModal.vue'),
		RunDiffusionWithRunnitMarkLogo,
		LoadingState,
		RunnitBoardSkeleton,
		Page404,
		RunnitSideNav,
		DomainClaimResolutionContent,
		Resources,
		NotificationsContainer,
		NotificationsBtn,
		FileBrowserBtn,
		ServerBuddy,
		SessionBar,
		AdminUserStats,
		UnsupportedDialog,
		UpsellCreatorsClubDialog,
		UpsellRunnitsPlanDialog,
		AccountMenuBtn,
		GetSupportBtn,
		SettingsBtn,
		FileBrowserNewTabBtn,
		GlobalSCSS,
		RunnitsSCSS,
		BaseDrawerSCSS,
		NewUserLoginPromoDialog,
		NewUserOnboardingDialog,
		MultipleSessions,
		TimeExpiringDialog,
		GlobalSnackbar,
		GlobalLoader,
		UserAccount,
		Toolbar,
		ComplexBackgroundMobileToolbar,
		ConfigureRunnit,
		RunnitTemplateDialog,
		DeleteRunnitBoardDialog,
		RunnitManageAccessDialog,
		RunnitModelsUploadDialog,
		VersionTracker,
		DeviceAndLoginMethodProtector,
		AnnouncementsBanner,
		DialogOrBottomSheet,
		DialogContent,
		ActionsIsland,
		ListItem,
	},
});
